import { TimePicker } from 'antd'
import moment from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { ActionEnum, getMinutes } from '../../../common'
import {
  ColumnActionHeader,
  ColumnInputHeader,
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { translate } from '../../../languages'
import { ShiftEntity } from '../../../models'
import { RootState } from '../../../stores'
import {
  loadShifts,
  setAction,
  setSelectedShift,
  setShiftFilter,
  setShowSearchDrawer,
} from '../../../stores/actions'
import {
  ManageShiftModalComponent,
  SearchShiftDrawerComponent,
} from './components'

export const ShiftPage: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { shifts, isShiftsLoaded, shiftFilter } = useSelector(
    (state: RootState) => state.shift
  )
  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  const [filter, setFilter] = React.useState(shiftFilter)

  React.useEffect(() => {
    if (isDesktopScreen) {
      dispatch(setShowSearchDrawer(false))
    }
    // eslint-disable-next-line
  }, [isDesktopScreen])

  React.useEffect(() => {
    dispatch(loadShifts())
    // eslint-disable-next-line
  }, [])

  React.useEffect(() => {
    setFilter(shiftFilter)
  }, [shiftFilter])

  const filteredShifts = React.useMemo(
    () =>
      shifts.filter(
        (shift) =>
          shift.name.toLowerCase().includes(shiftFilter.name.toLowerCase()) &&
          (!shiftFilter.startTime ||
            getMinutes(shift.startTime) * 60000 >= shiftFilter.startTime) &&
          (!shiftFilter.endTime ||
            getMinutes(shift.endTime) * 60000 <= shiftFilter.endTime) &&
          shift.station.name.toLowerCase().includes(shiftFilter.stationName)
      ),
    [shifts, shiftFilter]
  )

  const columns = React.useMemo(
    () => [
      {
        dataIndex: 'id',
        title: translate('shift.id', language),
        width: 30,
      },
      {
        dataIndex: 'name',
        title: (
          <ColumnInputHeader
            titleKey="shift.name"
            value={filter.name}
            onChange={(e) => setFilter({ ...filter, name: e.target.value })}
          />
        ),
        width: 130,
      },
      {
        dataIndex: 'startTime',
        title: (
          <>
            <span>{translate('shift.startTime', language)}</span>
            {isDesktopScreen && (
              <TimePicker
                style={{ width: '100%' }}
                format="HH:mm"
                placeholder={translate('shift.startTime', language)}
                showNow={false}
                value={
                  filter.startTime
                    ? moment(
                        `${filter.startTime / 3600000}:${
                          (filter.startTime % 3600000) / 60000
                        }`,
                        'HH:mm'
                      )
                    : undefined
                }
                onChange={(_, dateString) => {
                  setFilter({
                    ...filter,
                    startTime: dateString
                      ? getMinutes(dateString) * 60000
                      : undefined,
                  })
                }}
              />
            )}
          </>
        ),
        width: 130,
      },
      {
        dataIndex: 'endTime',
        title: (
          <>
            <span>{translate('shift.endTime', language)}</span>
            {isDesktopScreen && (
              <TimePicker
                style={{ width: '100%' }}
                format="HH:mm"
                placeholder={translate('shift.endTime', language)}
                showNow={false}
                value={
                  filter.endTime
                    ? moment(
                        `${filter.endTime / 3600000}:${
                          (filter.endTime % 3600000) / 60000
                        }`,
                        'HH:mm'
                      )
                    : undefined
                }
                onChange={(_, dateString) => {
                  setFilter({
                    ...filter,
                    endTime: dateString
                      ? getMinutes(dateString) * 60000
                      : undefined,
                  })
                }}
              />
            )}
          </>
        ),
        width: 130,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="shift.station"
            value={filter.stationName}
            onChange={(e) =>
              setFilter({ ...filter, stationName: e.target.value })
            }
          />
        ),
        width: 130,
        render: (_: any, shift: ShiftEntity) => shift.station.name,
      },
      {
        title: (
          <ColumnActionHeader
            onSearch={() => {
              dispatch(
                setShiftFilter({
                  ...filter,
                  startTime: filter.startTime
                    ? filter.endTime && filter.startTime > filter.endTime
                      ? filter.endTime
                      : filter.startTime
                    : undefined,
                  endTime: filter.endTime
                    ? filter.startTime && filter.endTime < filter.startTime
                      ? filter.startTime
                      : filter.endTime
                    : undefined,
                })
              )
            }}
          />
        ),
        render: (_: any, shift: ShiftEntity) => (
          <TableActionColumnButtonComponent
            onViewButtonClick={() => {
              dispatch(setSelectedShift(shift))
              dispatch(setAction(ActionEnum.VIEW))
            }}
            onDeleteButtonClick={() => {
              dispatch(setSelectedShift(shift))
              dispatch(setAction(ActionEnum.DELETE))
            }}
          />
        ),
        width: 150,
      },
    ],
    [dispatch, filter, isDesktopScreen, language]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {management.showManageModal && <ManageShiftModalComponent />}
      <PageTitleComponent
        onCreateButtonClick={() => {
          dispatch(setSelectedShift())
          dispatch(setAction(ActionEnum.CREATE))
        }}
        hasSearchDrawer
      />
      {management.showSearchDrawer && <SearchShiftDrawerComponent />}
      <TableComponent
        data={filteredShifts}
        columns={columns}
        loading={!isShiftsLoaded}
      />
    </div>
  )
}
